import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlarmRuleTable } from './AlarmRuleTable';
import { useAlarmList } from '../useAlarmList';
import { getAlarmRuleErrorMessage } from '../filterParameterMapping';

export const AlarmRuleList = ({ isActive = false, onView, onEdit, onRunReport, alarmRuleService, alarmNotificationService: toasterService, userService }) => {
	const [showArchived, setShowArchived] = useState(false);
	const [showActive, setShowActive] = useState(true);
	const [showInactive, setShowInactive] = useState(true);
	const [currentUserId] = useState(() => userService.getCurrentUserId());
	const { items, loadBusy, reload, updateItems, mountedRef } = useAlarmList({
		service: alarmRuleService,
		isActive,
		showArchived,
		toasterService,
		errorTitle: 'Failed to load alarm rules',
	});

	const handleEdit = async rule => {
		try {
			const saved = await onEdit(rule);
			if (!saved || !mountedRef.current) return;
			toasterService.success(`Alarm rule "${rule.ruleName}" updated`);
			reload();
		} catch (error) {
			if (!mountedRef.current) return;
			toasterService.error('Failed to edit alarm rule', getAlarmRuleErrorMessage(error, 'Please try again.'));
		}
	};

	const handleArchive = async rule => {
		const unarchive = !!rule.archivedTs;
		const action = unarchive ? 'unarchive' : 'archive';
		try {
			if (unarchive) {
				await alarmRuleService.unarchive(rule.id);
			} else {
				await alarmRuleService.archive(rule.id);
			}
			if (!mountedRef.current) return;
			toasterService.success(`Alarm rule "${rule.ruleName}" ${unarchive ? 'unarchived' : 'archived'}`);
			reload();
		} catch (error) {
			if (!mountedRef.current) return;
			toasterService.error(
				`Failed to ${action} alarm rule`,
				getAlarmRuleErrorMessage(error, `The alarm rule could not be ${action}d. Please try again.`)
			);
		}
	};

	const handleToggleActive = async rule => {
		if (rule.archivedTs) {
			toasterService.warning(`Alarm rule "${rule.ruleName}" is archived and cannot be updated`);
			return;
		}
		const nextActive = !rule.isActive;
		try {
			await alarmRuleService.setActive(rule.id, nextActive);
			if (!mountedRef.current) return;
			updateItems(rules => rules.map(existingRule => (
				existingRule.id === rule.id ? { ...existingRule, isActive: nextActive } : existingRule
			)));
			toasterService.success(`Alarm rule "${rule.ruleName}" ${nextActive ? 'activated' : 'deactivated'}`);
		} catch (error) {
			if (!mountedRef.current) return;
			toasterService.error(
				'Failed to update alarm rule status',
				getAlarmRuleErrorMessage(error, 'The alarm rule status could not be updated. Please try again.')
			);
		}
	};

	const handleToggleSubscribe = async rule => {
		const subscribedUserIds = rule.subscribedUserIds || [];
		const subscribed = !subscribedUserIds.includes(currentUserId);
		try {
			await alarmRuleService.subscribe(rule.id, currentUserId, subscribed);
			if (!mountedRef.current) return;
			updateItems(rules => rules.map(existingRule => (
				existingRule.id === rule.id ? {
					...existingRule,
					subscribedUserIds: subscribed
						? [...(existingRule.subscribedUserIds || []).filter(userId => userId !== currentUserId), currentUserId]
						: (existingRule.subscribedUserIds || []).filter(userId => userId !== currentUserId),
				} : existingRule
			)));
			toasterService.success(`Alarm rule "${rule.ruleName}" ${subscribed ? 'subscribed' : 'unsubscribed'}`);
		} catch (error) {
			if (!mountedRef.current) return;
			toasterService.error(
				'Failed to update alarm rule subscription',
				getAlarmRuleErrorMessage(error, 'The alarm rule subscription could not be updated. Please try again.')
			);
		}
	};

	const visibleRules = items
		.filter(rule => showArchived || !rule.archivedTs)
		.filter(rule => (rule.isActive ? showActive : showInactive));

	return (
		<AlarmRuleTable
			rules={visibleRules}
			isActive={isActive}
			loadBusy={loadBusy}
			showArchived={showArchived}
			onShowArchivedChange={setShowArchived}
			showActive={showActive}
			onShowActiveChange={setShowActive}
			showInactive={showInactive}
			onShowInactiveChange={setShowInactive}
			onView={onView}
			onEdit={handleEdit}
			onArchive={handleArchive}
			onToggleActive={handleToggleActive}
			onRunReport={onRunReport}
			onToggleSubscribe={handleToggleSubscribe}
			currentUserId={currentUserId}
		/>
	);
};

AlarmRuleList.propTypes = {
	isActive: PropTypes.bool,
	onView: PropTypes.func.isRequired,
	onEdit: PropTypes.func.isRequired,
	onRunReport: PropTypes.func.isRequired,
	alarmRuleService: PropTypes.object.isRequired,
	alarmNotificationService: PropTypes.object.isRequired,
	userService: PropTypes.object.isRequired,
};